import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { Tag } from "../../../../../../../shared/frontend/ui";
import React from "react";
import type { LocalizedText } from "../../../app/i18n/i18n";
import type { SortableColumnExtension } from "../../../app/utils/proTableSortPersistence";

export type StatusTone = "success" | "default" | "processing" | "warning" | "danger" | "info";

export type StatusTagProps = {
  label: React.ReactNode;
  tone?: StatusTone;
};

const toneColor: Record<StatusTone, string | undefined> = {
  success: "green",
  default: undefined,
  processing: "processing",
  warning: "gold",
  danger: "red",
  info: "blue",
};

export function StatusTag({ label, tone = "default" }: StatusTagProps) {
  return <Tag color={toneColor[tone]} style={{ marginInlineEnd: 0 }}>{label}</Tag>;
}

export type TextResolver = (value: LocalizedText) => string;

export function activeStatusColumn<T extends object>(
  text: TextResolver,
  {
    key = "isActive",
    dataIndex = "isActive",
    title = "Status",
    activeLabel = "active",
    inactiveLabel = "inactive",
    width = 120,
  }: {
    key?: string;
    dataIndex?: string;
    title?: LocalizedText;
    activeLabel?: LocalizedText;
    inactiveLabel?: LocalizedText;
    width?: number;
  } = {}
): ListColumn<T> {
  return {
    title: text(title),
    dataIndex,
    key,
    width,
    render: (_: unknown, record: T) => {
      const active = Boolean((record as Record<string, unknown>)[dataIndex]);
      return <StatusTag tone={active ? "success" : "default"} label={text(active ? activeLabel : inactiveLabel)} />;
    },
  };
}

export function makeStatusColumn<T>(
  text: TextResolver,
  {
    key = "status",
    title = "Status",
    width = 120,
    getStatus,
  }: {
    key?: string;
    title?: LocalizedText;
    width?: number;
    getStatus: (record: T) => { label: LocalizedText; tone?: StatusTone };
  }
): ListColumn<T> & SortableColumnExtension<T> {
  return {
    title: text(title),
    key,
    width,
    // Der Status entsteht erst im render — ohne diesen Sortierwert bliebe die Spalte unsortierbar.
    sortValue: (record: T) => text(getStatus(record).label),
    render: (_: unknown, record: T) => {
      const status = getStatus(record);
      return <StatusTag tone={status.tone} label={text(status.label)} />;
    },
  };
}
